import { ReactNode, useContext } from 'react';
import AccordionContext from 'react-bootstrap/AccordionContext';
import { Accordion } from 'components/DesignSystems/Accordion/Accordion';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { useUscgMapContext } from './UscgMapContext';
import { fetchInternalLayerVersions } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import { useCreateLocation } from 'hooks/icsLocation/useCreateLocation';
import { useUpdateLocation } from 'hooks/icsLocation/useUpdateLocation';
import type { IcsIncidentLocation } from 'hooks/icsLocation/icsIncidentLocation.types';
import { LayerFeaturesEditor } from './LayerFeaturesEditor';
import classNames from 'classnames';
import { WorkspaceNameBadge } from './LayerWorkspaceBadge';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InternalLayer } from 'hooks/internalLayer/types';
import { PointGeometry } from './utils/constants';
import { NULL_VALUE } from 'constants/displayConstants';
import { IconButton } from 'components/DesignSystems/buttons/IconButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { type LocationCustomField } from './utils/locationPopup';

export function PropertyRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <div className="d-flex small gap-2">
      <span className="text-muted">{label}:</span>
      <span>{value ?? NULL_VALUE}</span>
    </div>
  );
}

function LayerHeader({
  title,
  nameEditor,
  workspaceName,
  layerId,
  layerType,
  provenanceBadge,
  error,
  onOpenInNew,
  onFocusView,
  onPermissions,
  onZoomToLayer,
  zoomTour,
  qualityTour,
  onDelete,
  deleteLabel = 'Delete',
  onPublish,
  onApprove,
  publishLabel = 'Publish',
  publishDisabled,
  pickPayload,
}: {
  title: ReactNode;
  nameEditor?: ReactNode;
  workspaceName?: string | null;
  layerId: string;
  layerType: 'internal' | 'external';
  /** Agency or platform serving the data, e.g. "ArcGIS", "USDA". */
  provenanceBadge?: string;
  error?: string;
  onOpenInNew?: () => void;
  onFocusView?: () => void;
  onPermissions?: () => void;
  onZoomToLayer?: () => void;
  /** `data-tour` on the zoom control for product tours. */
  zoomTour?: string;
  /** `data-tour` on the provenance / quality badge for product tours. */
  qualityTour?: string;
  onDelete?: () => void;
  deleteLabel?: string;
  /** Publishes a draft so every workspace in the organization can see it. */
  onPublish?: () => void;
  onApprove?: () => void;
  publishLabel?: string;
  publishDisabled?: boolean;
  /** When set, header click in AI pick mode adds this layer as a data source. */
  pickPayload?: {
    name: string;
    versionId?: string;
    serviceUrl?: string | null;
    arcgisItemId?: string | null;
  };
}) {
  const {
    isInternalLayerVisible,
    isExternalLayerVisible,
    toggleInternalLayerVisibility,
    toggleExternalLayerVisibility,
    aiLayerPickMode,
    pickAiLayer,
  } = useUscgMapContext();
  const accordion = useContext(AccordionContext);
  const headerExpanded = Boolean(accordion?.activeEventKey);

  const isVisible =
    layerType === 'internal'
      ? isInternalLayerVisible(layerId)
      : isExternalLayerVisible(layerId);

  const badgeLabel = provenanceBadge ?? 'ArcGIS';
  const isAgencyBadge = badgeLabel !== 'ArcGIS';

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (layerType === 'internal') {
      toggleInternalLayerVisibility(layerId);
    } else {
      toggleExternalLayerVisibility(layerId);
    }
  };

  const handlePickClick = (e: React.MouseEvent) => {
    if (!aiLayerPickMode || !pickPayload) return;
    e.stopPropagation();
    e.preventDefault();
    pickAiLayer({
      kind: layerType,
      id: layerId,
      name: pickPayload.name,
      versionId: pickPayload.versionId,
      serviceUrl: pickPayload.serviceUrl,
      arcgisItemId: pickPayload.arcgisItemId,
    });
  };

  return (
    <div
      className={classNames(
        'd-flex justify-content-between align-items-center w-100',
        { 'layers-ai-pick-target': aiLayerPickMode && !!pickPayload }
      )}
      onClick={handlePickClick}
    >
      <div className="d-flex align-items-center min-w-0 flex-grow-1 gap-2">
        <StylishNewCheckbox
          className="mt-0"
          checked={isVisible}
          onClick={handleToggle}
        />
        {headerExpanded && nameEditor ? (
          nameEditor
        ) : (
          <span className="fw-semibold tw:min-w-0 tw:truncate">{title}</span>
        )}
        {workspaceName ? <WorkspaceNameBadge name={workspaceName} /> : null}
      </div>
      <div className="d-flex align-items-center gap-1">
        {layerType === 'external' && (
          <>
            <div
              className={classNames('rounded border px-2 py-1', {
                'text-success border-success': !isAgencyBadge,
                'text-info border-info': isAgencyBadge,
              })}
              style={{
                background: isAgencyBadge
                  ? 'rgba(56, 189, 248, 0.125)'
                  : 'rgba(34, 197, 94, 0.125)',
              }}
              {...(qualityTour ? { 'data-tour': qualityTour } : {})}
            >
              {badgeLabel}
            </div>
            {error && (
              <OverlayTrigger
                placement="top"
                overlay={(props) => <Tooltip {...props}>{error}</Tooltip>}
              >
                <IconButton
                  iconProps={{
                    iconName: 'error',
                    color: 'danger',
                  }}
                  aria-label="Error message"
                  title="Error message"
                />
              </OverlayTrigger>
            )}
          </>
        )}
        {onZoomToLayer && (
          <IconButton
            iconProps={{ iconName: 'map' }}
            onClick={(e) => {
              e.stopPropagation();
              onZoomToLayer();
            }}
            aria-label="Zoom to layer"
            title="Zoom to layer"
            {...(zoomTour ? { 'data-tour': zoomTour } : {})}
          />
        )}
        {(onOpenInNew ||
          onFocusView ||
          onPermissions ||
          onDelete ||
          onApprove ||
          onPublish) && (
          <Dropdown
            align="end"
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0"
          >
            <Dropdown.Toggle
              className="button--icon"
              id={`layer-actions-${layerType}-${layerId}`}
              onClick={(e) => e.stopPropagation()}
              aria-label="Layer actions"
              title="Layer actions"
            >
              <SharedIcon iconName="more_vert" />
            </Dropdown.Toggle>
            <Dropdown.Menu
              align="end"
              popperConfig={{ strategy: 'fixed' }}
              onClick={(e) => e.stopPropagation()}
            >
              {onFocusView && (
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    onFocusView();
                  }}
                >
                  Focused View
                </Dropdown.Item>
              )}
              {onPermissions && (
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    onPermissions();
                  }}
                >
                  Permissions
                </Dropdown.Item>
              )}
              {onOpenInNew && (
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenInNew();
                  }}
                >
                  <SharedIcon iconName="open_in_new" />
                  View details
                </Dropdown.Item>
              )}
              {onPublish && (
                <Dropdown.Item
                  disabled={publishDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPublish();
                  }}
                >
                  <SharedIcon iconName="publish" />
                  {publishLabel}
                </Dropdown.Item>
              )}
              {onApprove && (
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove();
                  }}
                >
                  Approve
                </Dropdown.Item>
              )}
              {onDelete && (
                <Dropdown.Item
                  data-testid="delete-layer-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <SharedIcon iconName="delete" />
                  {deleteLabel}
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    </div>
  );
}

export function FeatureDetailRows({
  lastUpdated,
  point,
  customFields,
}: {
  lastUpdated?: string | null;
  point?: PointGeometry;
  customFields?: LocationCustomField[];
}) {
  return (
    <>
      <PropertyRow label="Last Updated" value={lastUpdated} />
      <PropertyRow label="Latitude" value={point?.latitude.toString()} />
      <PropertyRow label="Longitude" value={point?.longitude.toString()} />
      {customFields?.map((field, index) => (
        <PropertyRow
          key={`${field.label}-${index}`}
          label={field.label || 'Field'}
          value={field.value}
        />
      ))}
    </>
  );
}

type LayerFeaturesListProps = {
  isLoading?: boolean;
  internalLayer?: InternalLayer | null;
  showAddObjectButton?: boolean;
  searchTerm?: string;
  onChangeSearchTerm?: (term: string) => void;
  children?: ReactNode;
  showSearch?: boolean;
  editingLocation?: IcsIncidentLocation | null;
  onCancelEdit?: () => void;
};

function LayerFeaturesList({
  isLoading,
  children,
  internalLayer,
  showAddObjectButton,
  searchTerm,
  onChangeSearchTerm,
  showSearch,
  editingLocation,
  onCancelEdit,
}: LayerFeaturesListProps) {
  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation();
  const { setSelectedInternalLayerVersion, setInternalLayerVisible } =
    useUscgMapContext();
  const selectSavedVersion = async (versionId?: string | null) => {
    if (!internalLayer) return;
    setInternalLayerVisible(internalLayer.id, true);
    if (!versionId) return;
    const versions = await fetchInternalLayerVersions({
      filter: { internalLayerId: internalLayer.id },
    });
    const match = versions.data.find((version) => version.id === versionId);
    if (!match) return;
    setSelectedInternalLayerVersion(internalLayer.id, match);
  };

  if (isLoading) {
    return (
      <p className="small text-muted mb-0 p-2 pt-0">Loading features...</p>
    );
  }

  return (
    <>
      <LayerFeaturesEditor
        internalLayer={internalLayer}
        showAddObjectButton={showAddObjectButton}
        searchTerm={searchTerm}
        onChangeSearchTerm={onChangeSearchTerm}
        editingLocation={editingLocation}
        onCancelEdit={onCancelEdit}
        onSubmitLocation={async ({ location }) => {
          if (editingLocation) {
            const updated = await updateLocation.mutateAsync({
              ...location,
              id: editingLocation.id,
            });
            await selectSavedVersion(updated?.internalLayerVersionId);
            onCancelEdit?.();
            return;
          }
          const created = await createLocation.mutateAsync(location);
          await selectSavedVersion(created.internalLayerVersionId);
        }}
        showSearch={showSearch}
      />
      {children}
    </>
  );
}

export const Layer = {
  Item: ({
    eventKey,
    header,
    children,
    cardClassName = '',
    defaultExpanded = false,
    dataTour,
  }: {
    eventKey: string;
    header: ReactNode;
    children: ReactNode;
    cardClassName?: string;
    defaultExpanded?: boolean;
    /** `data-tour` on the layer row for product tours. */
    dataTour?: string;
  }) => (
    <li
      className="opacity-hover-reveal position-relative list-unstyled"
      {...(dataTour ? { 'data-tour': dataTour } : {})}
    >
      <Accordion
        classes={{
          card: classNames(
            'border-neutral tw:!border-solid tw:has-[.show]:!border-primary',
            cardClassName
          ),
        }}
        eventKey={eventKey}
        header={header}
        defaultExpanded={defaultExpanded}
      >
        {children}
      </Accordion>
    </li>
  ),
  Header: LayerHeader,
  Content: ({
    children,
    className = '',
  }: {
    children: ReactNode;
    className?: string;
  }) => (
    <div className={classNames('d-grid gap-2 px-4 pb-4', className)}>
      {children}
    </div>
  ),
  FeaturesList: ({ children, ...props }: LayerFeaturesListProps) => (
    <LayerFeaturesList {...props}>{children}</LayerFeaturesList>
  ),
};
